import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  IconButton,
  Tooltip,
  MenuItem,
  Typography,
  Menu,
} from "@mui/material";
import { message } from "antd";
import { useDispatch } from "react-redux";
import { logout } from "../../redux/reduxSlice/authSlice";
import { Link, useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import Avatar from "../../components/profileAvatar/Avatar";
import config from "../../utils/config";
import Logout from "../../components/modal/logout";

const AuthButton = ({ handleNavigation }) => {
  const location = useLocation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [loading, setLoading] = useState(false);

  const settings = ["Profile", "Dashboard", "Logout"];
  const [anchorElUser, setAnchorElUser] = useState(null);
  const [isLogoutModalOpen, setIsLogoutModalOpen] = useState(false);

  const handleLogoutConfirm = async () => {
    setIsLogoutModalOpen(false);
    setLoading(true);
    try {
      await new Promise((resolve) => setTimeout(resolve, 1000));
      dispatch(logout());
      localStorage.removeItem("token");
      localStorage.removeItem("userId");
      localStorage.removeItem("gender");
      setIsLoggedIn(false);
      navigate("/login");
      // ✅ Removed message.success here, since it's already shown inside the Logout modal
    } catch (error) {
      // ❌ Remove this as well, since Logout modal shows the error message
    } finally {
      setLoading(false);
    }
  };

  const handleLogoutCancel = () => {
    setIsLogoutModalOpen(false);
  };

  const handleOpenUserMenu = (event) => {
    event.stopPropagation(); // Prevent bubbling
    setAnchorElUser(event.currentTarget); // Always allow menu to open
  };
  // Dashboard click handler (gender check)
  const handleDashboardClick = () => {
    const gender = user?.gender || localStorage.getItem("gender");

    if (gender?.trim()) {
      navigate("/dashboard");
    } else {
      message.warning("Please update your profile before accessing Dashboard.");
      navigate("/profile");
    }
    handleCloseUserMenu();
  };

  const handleCloseUserMenu = () => {
    setAnchorElUser(null);
  };

  const handleMenuItemClick = (setting) => {
    handleCloseUserMenu();
    if (setting === "Logout") {
      setIsLogoutModalOpen(true); 
    } else {
      handleNavigation(`/${setting.toLowerCase()}`);
    }
  };

  useEffect(() => {
    // First check if userId exists in localStorage
    const userId = localStorage.getItem("userId");

    if (userId) {
      // Only if userId exists, set logged in and fetch user data
      setIsLoggedIn(true);

      const fetchUserData = async () => {
        try {
          const response = await axios.get(
            `${config.apiBaseUrl}/auth/getUser?id=${userId}`
          );
          setUser(response.data.user);
        } catch (error) {
          console.error("Error fetching user data:", error);
          // If API fails, we might want to handle invalid tokens
          if (
            error.response &&
            (error.response.status === 401 || error.response.status === 403)
          ) {
            localStorage.removeItem("token");
            localStorage.removeItem("userId");
            setIsLoggedIn(false);
          }
        }
      };

      fetchUserData();
    } else {
      // No userId means not logged in
      setIsLoggedIn(false);
    }
  }, [location.pathname]);

  const renderBMUserPart = () => (
    <Box className="bm_user">
      <Tooltip title="Open settings">
        <IconButton
          onClick={handleOpenUserMenu}
          sx={{ p: 0 }}
          aria-label="User Settings"
        >
          <Avatar
            name={
              user?.firstName && user?.lastName
                ? `${user.firstName} ${user.lastName}`
                : ""
            }
          />
        </IconButton>
      </Tooltip>
      <Menu
        sx={{ mt: "45px" }}
        id="menu-appbar"
        anchorEl={anchorElUser}
        anchorOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        keepMounted
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        open={Boolean(anchorElUser)}
        onClose={handleCloseUserMenu}
      >
        {settings.map((setting) => (
          <MenuItem
            key={setting}
            // onClick={() => handleMenuItemClick(setting)}
            onClick={() =>
              setting === "Dashboard"
                ? handleDashboardClick()
                : handleMenuItemClick(setting)
            }
            disabled={loading && setting === "Logout"}
          >
            <Typography textAlign="center">{setting}</Typography>
          </MenuItem>
        ))}
      </Menu>

      {/* Logout Modal */}
      <Logout
        open={isLogoutModalOpen}
        handleOk={handleLogoutConfirm}
        handleCancel={handleLogoutCancel}
      />
    </Box>
  );

  const publicRoutes = [
    "/",
    "/about",
    "/service",
    "/contact",
    "/pricing",
    "/pricePackage",
    "/about-us",
    "/careers",
    "/blog",
    "/privacy-policy",
    "/terms-and-conditions",
    "/marketing",
    "/research",
    "/ios-&-android",
  ];

  const dashboardRoutes = [
    "/dashboard",
    "/visual",
    "/symptom",
    "/profile",
    "/healthHistory",
    "/visual-nhsi",
  ];

  const isPaymentSuccess = location.pathname.startsWith("/paymentSuccess");

  const shouldRenderBMUserPart =
    isLoggedIn &&
    (publicRoutes.includes(location.pathname) ||
      dashboardRoutes.includes(location.pathname) ||
      isPaymentSuccess);

  const isLoginRoute = location.pathname === "/login";
  const isSignupRoute = location.pathname === "/signup";

  if (shouldRenderBMUserPart) {
    return renderBMUserPart();
  } else if (isLoginRoute) {
    return (
      <Button
        component={Link}
        to="/signup"
        className="header_signin_button"
        sx={{
          "@media (max-width: 902px)": {
            fontWeight: "600",
            color: "white",
            margin: "1rem",
          },
        }}
      >
        Signup
      </Button>
    );
  } else if (isSignupRoute) {
    return (
      <Button
        component={Link}
        to="/login"
        className="header_signin_button"
        sx={{
          "@media (max-width: 902px)": {
            fontWeight: "600",
            color: "white",
            margin: "1rem",
          },
        }}
      >
        Login
      </Button>
    );
  } else {
    return (
      <Button
        component={Link}
        to="/login"
        className="header_signin_button"
        sx={{
          "@media (max-width: 902px)": {
            fontWeight: "600",
            color: "white",
            margin: "1rem",
          },
        }}
      >
        Login
      </Button>
    );
  }
};

export default AuthButton;
